import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Download, X } from 'lucide-react';
import { proposalService } from '../services/proposalService';
import { clientService } from '../services/clientService';
import { settingsService } from '../services/settingsService';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const PublicProposalView = () => {
  const { token } = useParams();
  const viewRef = useRef(null);
  const [proposal, setProposal] = useState(null);
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [companyData, setCompanyData] = useState({ name: '', data: '', logo: '' });

  useEffect(() => {
    const loadProposal = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Cargar propuesta compartida (sin autenticación)
        const proposalData = await proposalService.getSharedProposal(token);
        
        // Normalizar datos del backend (snake_case a camelCase)
        const normalizedProposal = {
          ...proposalData,
          contactId: proposalData.contact_id || proposalData.contactId,
          proposalId: proposalData.proposal_id || proposalData.proposalId,
          createdAt: proposalData.created_at || proposalData.createdAt,
          items: proposalData.items || [],
          totalAmount: proposalData.total_amount || 0,
        };
        
        setProposal(normalizedProposal);

        // Cargar contacto si existe
        const contactId = proposalData.contact_id || proposalData.contactId;
        if (contactId) {
          try {
            const contactData = await clientService.getById(contactId, { skipAuth: true });
            setContact(contactData);
          } catch (err) {
            console.warn('No se pudo cargar el contacto:', err);
          }
        }

      } catch (err) {
        console.error('Error cargando cotización:', err);
        setError('Cotización no encontrada o token inválido');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      loadProposal();
    }
  }, [token]);

  // Cargar datos de empresa al montar
  useEffect(() => {
    const loadCompanyData = async () => {
      try {
        const data = await settingsService.getCompanyData();
        setCompanyData(data);
      } catch (error) {
        console.error('Error cargando datos de empresa:', error);
      }
    };
    loadCompanyData();
  }, []);

  const formatCurrency = (amount) => {
    if (!proposal) return '$0.00';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const handleGeneratePDF = async () => {
    if (!viewRef.current) return;
    
    setGeneratingPDF(true);
    try {
      const canvas = await html2canvas(viewRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgScaledWidth = imgWidth * ratio;
      const imgScaledHeight = imgHeight * ratio;
      
      let heightLeft = imgScaledHeight;
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, imgScaledWidth, imgScaledHeight);
      heightLeft -= pdfHeight;
      
      while (heightLeft > 0) {
        position = heightLeft - imgScaledHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgScaledWidth, imgScaledHeight);
        heightLeft -= pdfHeight;
      }
      
      pdf.save(`cotizacion-${proposal.proposalId || 'cotizacion'}.pdf`);
    } catch (error) {
      console.error('Error generando PDF:', error);
      alert('Error al generar el PDF');
    } finally {
      setGeneratingPDF(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando cotización...</p>
        </div>
      </div>
    );
  }

  if (error || !proposal) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center bg-white rounded-2xl p-8 shadow-lg max-w-md">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Cotización no encontrada</h1>
          <p className="text-gray-600 mb-6">{error || 'La cotización solicitada no existe o el link ha expirado.'}</p>
          <a href="/" className="text-blue-600 hover:text-blue-800 underline">Volver al inicio</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="bg-white rounded-2xl p-6 mb-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Cotización {proposal.proposalId}</h1>
              <p className="text-gray-600 mt-1">Vista pública - Solo lectura</p>
            </div>
            <button
              onClick={handleGeneratePDF}
              disabled={generatingPDF}
              className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all font-medium flex items-center gap-2 disabled:opacity-50"
            >
              {generatingPDF ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Generando...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Descargar PDF
                </>
              )}
            </button>
          </div>
        </div>

        {/* Contenido de la cotización */}
        <div
          id={`proposal-view-${proposal.id}`}
          ref={viewRef}
          className="bg-white mx-auto rounded-2xl shadow-lg p-8"
          style={{ 
            width: '210mm',
            maxWidth: '210mm'
          }}
        >
          {/* Primera hoja A4 - Cotización Económica */}
          <div 
            className="proposal-page bg-white mb-4 shadow-lg relative"
            style={{
              width: '210mm',
              minHeight: '297mm',
              maxHeight: '297mm',
              overflow: 'auto',
              pageBreakAfter: 'always',
              boxSizing: 'border-box',
              paddingLeft: '15mm',
              paddingRight: '15mm',
              paddingTop: '15mm',
              paddingBottom: '50mm'
            }}
          >
            {/* Logo de la empresa en margen superior izquierdo */}
            {companyData.logo && (
              <div 
                style={{
                  position: 'absolute',
                  top: '8mm',
                  left: '8mm',
                  zIndex: 10,
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <img
                  src={companyData.logo}
                  alt="Logo de la empresa"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    opacity: 1
                  }}
                />
              </div>
            )}
            
            {/* Encabezado Corporativo */}
            <div className="proposal-header no-break">
              <div className="text-right mt-3">
                <div className="proposal-subtitle">
                  {(() => {
                    const date = new Date(proposal.createdAt);
                    const months = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
                    return `Buenos Aires, ${date.getDate()} de ${months[date.getMonth()]} de ${date.getFullYear()}`;
                  })()}
                </div>
              </div>
            </div>

            {/* Saludo formal */}
            <div className="mb-4 proposal-section no-break">
              <p className="text-sm text-gray-800 mb-2 proposal-paragraph no-break" style={{ lineHeight: '1.6' }}>
                <strong>De nuestra mayor consideración:</strong>
              </p>
              {contact?.name && (
                <p className="text-sm text-gray-800 mb-2 proposal-paragraph no-break" style={{ lineHeight: '1.6' }}>
                  {(proposal.salutation || 'Sr.')} {contact.name}
                </p>
              )}
              {proposal.greeting ? (
                <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line proposal-paragraph no-break" style={{ lineHeight: '1.7', textAlign: 'justify' }}>
                  {proposal.greeting.replace(proposal.proposalId ? `(Ref. ${proposal.proposalId})` : '', '').trim()}
                  {proposal.proposalId && ` (Ref. ${proposal.proposalId})`}
                </p>
              ) : (
                <p className="text-sm text-gray-800 leading-relaxed proposal-paragraph no-break" style={{ lineHeight: '1.7', textAlign: 'justify' }}>
                  En virtud de lo conversado, Vinson tiene el agrado de presentarle una cotización comercial
                  {proposal.proposalId && ` (Ref. ${proposal.proposalId})`}
                  para la implementación de nuestras soluciones de Software POS.
                </p>
              )}
            </div>

            {/* Cotización - Items estructurados */}
            {proposal.items && proposal.items.length > 0 && (
              <div className="mb-4 proposal-section no-break">
                <h2 className="proposal-title mb-3 no-break" style={{ fontSize: '14px', marginTop: '20px' }}>PROPUESTA ECONÓMICA</h2>
                
                <table className="proposal-table-modern no-break">
                  <thead>
                    <tr>
                      <th>Producto</th>
                      <th style={{ textAlign: 'center', width: '60px' }}>Código</th>
                      <th style={{ textAlign: 'center', width: '70px' }}>Marca</th>
                      <th style={{ textAlign: 'center', width: '80px' }}>Cant. Pedida</th>
                      <th style={{ textAlign: 'right', width: '100px' }}>Precio FOB</th>
                      <th style={{ textAlign: 'right', width: '100px' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {proposal.items.map((item, idx) => (
                      <tr key={idx}>
                        <td style={{ fontWeight: 400, fontSize: '10px' }}>{item.product_name || '-'}</td>
                        <td style={{ textAlign: 'center', fontSize: '10px' }}>{item.product_code || '-'}</td>
                        <td style={{ textAlign: 'center', fontSize: '10px' }}>{item.brand_name || '-'}</td>
                        <td style={{ textAlign: 'center', fontSize: '10px' }}>{item.quantity || 0}</td>
                        <td style={{ textAlign: 'right', fontSize: '10px' }}>
                          {formatCurrency(item.price_fob_unit || 0)}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 500, fontSize: '10px' }}>
                          {formatCurrency(item.total || 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Total general */}
                <div className="proposal-total-box no-break proposal-section" style={{ marginTop: '24px', width: 'fit-content', marginLeft: 'auto' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '20px' }}>
                    <span className="proposal-total-label" style={{ fontSize: '12px' }}>TOTAL:</span>
                    <span style={{ fontSize: '14px', fontWeight: 700 }}>
                      {formatCurrency(proposal.items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0))}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Pie de página */}
            <div className="proposal-footer">
              <div className="proposal-footer-company">
                {companyData.name || 'VINSON TECH SRL – Sistemas de Gestión y POS'}
              </div>
              <div className="proposal-footer-contact">
                {companyData.data || 'Av. Corrientes 1234, CABA | Tel: (011) 1234-5678 | Email: info@vinson.com.ar'}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-xs text-gray-500">
          Powered by Vinson | © Vinson 2026
        </div>
      </div>
    </div>
  );
};

export default PublicProposalView;

